import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ChevronsUpDown, Info, Link as LinkIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { ExportTicketsTool } from "@/components/solution-engineer/export-tickets-tool";
import { IntegrationHealthTool } from "@/components/solution-engineer/integration-health-tool";
import { PeripheralScanTool } from "@/components/solution-engineer/peripheral-scan-tool";
import { ResyncTool } from "@/components/solution-engineer/resync-tool";
import { ServiceAccountsTool } from "@/components/solution-engineer/service-accounts-tool";
import { SsoSetupDialog } from "@/components/tenants/sso-setup-dialog";
import { TenantAdminsSection } from "@/components/tenants/tenant-admins-section";
import { useAllTenants } from "@/hooks/use-tenants";
import { isRemoteCellId, tenantLabel, tenantOptionValue } from "@/lib/cells/cells";

const searchSchema = z.object({
  tenantId: z.string().optional(),
});

export const Route = createFileRoute("/solution-engineer")({
  component: SolutionEngineerPage,
  validateSearch: zodValidator(searchSchema),
});

function SolutionEngineerPage() {
  const { tenantId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantOpen, setTenantOpen] = useState(false);

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();

  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-auto p-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">
          Solution Engineer
        </h1>
        <p className="text-muted-foreground">
          Run the recurring onboarding and support tools against one tenant.
        </p>
      </div>

      {/* Context strip: which tenant the tools run on */}
      <div className="bg-muted/30 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border p-4">
        <div className="space-y-1.5">
          <p className="text-muted-foreground/70 text-[10px] font-semibold uppercase tracking-widest">
            Tenant
          </p>
          <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={tenantOpen}
                className="bg-background w-[240px] justify-between"
              >
                <span className="truncate">
                  {selectedTenant
                    ? tenantLabel(selectedTenant)
                    : "Select a tenant..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0">
              <Command>
                <CellTenantsUnavailable failures={tenantFailures} />
                <CommandInput placeholder="Search tenants..." />
                <CommandList>
                  <CommandEmpty>
                    {tenantsLoading ? "Loading..." : "No tenant found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {sortedTenants.map((tenant) => (
                      <CommandItem
                        key={tenant.id}
                        value={tenantOptionValue(tenant)}
                        onSelect={() => {
                          void navigate({ search: { tenantId: tenant.id } });
                          setTenantOpen(false);
                        }}
                        className={cn(tenantId === tenant.id && "bg-accent")}
                      >
                        {tenantLabel(tenant)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {selectedTenant && (
          <SsoSetupDialog
            key={selectedTenant.id}
            tenant={selectedTenant}
            trigger={
              <Button variant="outline" className="bg-background ml-auto">
                <LinkIcon className="mr-2 h-4 w-4" />
                SSO setup link
              </Button>
            }
          />
        )}
      </div>

      {!tenantId ? (
        <div className="border-muted-foreground/25 text-muted-foreground flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm">
          <Info className="h-5 w-5 opacity-60" />
          Select a tenant to run tools on it.
        </div>
      ) : (
        // key resets every tool's state when the tenant changes
        <div key={tenantId} className="flex max-w-5xl flex-col gap-6 pb-10">
          {/* Same gate as the tenant page: the admins bootstrap only reaches primary-cell tenants */}
          {selectedTenant && !isRemoteCellId(selectedTenant.deployment_cell ?? undefined) && (
            <TenantAdminsSection
              tenantId={tenantId}
              tenantName={selectedTenant.company}
            />
          )}
          <PeripheralScanTool tenantId={tenantId} />
          <ServiceAccountsTool tenantId={tenantId} />
          <ExportTicketsTool tenantId={tenantId} />
          <ResyncTool tenantId={tenantId} />
          <IntegrationHealthTool tenantId={tenantId} />
        </div>
      )}
    </div>
  );
}
